import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlined from '@mui/icons-material/ThumbDownOutlined';
import { ToolbarButton } from '../inputs/ToolbarButton';

export type ChatFeedback = 'up' | 'down' | null;

export interface ChatMessageActionsProps {
  /** Copy the answer. The button confirms for a moment after. */
  onCopy?: () => void | Promise<void>;
  feedback?: ChatFeedback;
  /** Thumbs up or down; choosing the current one again clears it. */
  onFeedback?: (value: ChatFeedback) => void;
  /** More actions after these, e.g. Add to dashboard. */
  children?: React.ReactNode;
}

/** The quiet row of actions under a finished answer. */
export function ChatMessageActions({ onCopy, feedback = null, onFeedback, children }: ChatMessageActionsProps) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(id);
  }, [copied]);

  if (!onCopy && !onFeedback && !children) return null;

  const thumb = (value: 'up' | 'down', label: string, icon: React.ReactNode) => (
    <Tooltip title={label}>
      <IconButton
        size="small"
        aria-label={label}
        aria-pressed={feedback === value}
        onClick={() => onFeedback?.(feedback === value ? null : value)}
        sx={(t) => ({
          color: feedback === value ? t.tokens.text.accent : t.tokens.text.tertiary,
          bgcolor: feedback === value ? t.tokens.brand.primarySoft : 'transparent',
          '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.brand.primarySofter },
        })}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }}>
      {onCopy && (
        <ToolbarButton
          variant="ghost"
          icon={copied ? <CheckRounded fontSize="small" /> : <ContentCopyRounded fontSize="small" />}
          onClick={async () => {
            await onCopy();
            setCopied(true);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </ToolbarButton>
      )}
      {onFeedback && thumb('up', 'Good answer', <ThumbUpOutlined fontSize="small" />)}
      {onFeedback && thumb('down', 'Bad answer', <ThumbDownOutlined fontSize="small" />)}
      {children}
    </Box>
  );
}
